import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { catLottoLabel, ESITO } from "@/pages/FinderAste";
import { Loader2, Star, ExternalLink, Eye } from "lucide-react";
import { toast } from "sonner";

const STATO_OPTIONS = [
  { value: "da_valutare", label: "Da valutare", color: "#3B82F6" },
  { value: "interessante", label: "Interessante", color: "#10B981" },
  { value: "scartata", label: "Scartata", color: "#71717A" },
  { value: "offerta_presentata", label: "Offerta presentata", color: "#F59E0B" },
  { value: "aggiudicata", label: "Aggiudicata", color: "#22C55E" },
];
const statoInfo = (v) => STATO_OPTIONS.find((s) => s.value === v);

export default function LeMieAste() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/aste/le-mie-operazioni")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const togglePreferito = async (a) => {
    try {
      await api.put(`/aste/${a.id_annuncio}/preferito`, { preferito: !a.preferito });
      load();
    } catch {
      toast.error("Errore nell'aggiornamento dei preferiti");
    }
  };

  const visibili = filtroStato ? items.filter((a) => a.stato === filtroStato) : items;

  return (
    <div className="space-y-6" data-testid="le-mie-aste-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-white">Le mie operazioni</h1>
          <p className="text-sm text-zinc-500 mt-1">Aste preferite, classificate o con una nota</p>
        </div>
        <select
          value={filtroStato}
          onChange={(e) => setFiltroStato(e.target.value)}
          data-testid="le-mie-aste-filtro-stato"
          className="bg-[#122A3F] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]"
        >
          <option value="">Tutti gli stati</option>
          {STATO_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>
      ) : visibili.length === 0 ? (
        <div className="text-center py-24 text-zinc-500" data-testid="le-mie-aste-empty">
          Nessuna asta salvata — apri il dettaglio di un'asta in Finder Aste e clicca sulla stella o classificala per ritrovarla qui.
        </div>
      ) : (
        <div className="bg-[#122A3F] border border-white/10 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-white/10">
                  <th className="px-4 py-3 font-medium">Immobile</th>
                  <th className="px-4 py-3 font-medium">Data vendita / Esito</th>
                  <th className="px-4 py-3 font-medium text-right">Offerta min.</th>
                  <th className="px-4 py-3 font-medium">Stato</th>
                  <th className="px-4 py-3 font-medium">Nota</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((a) => {
                  const esitoInfo = ESITO[a.esito];
                  const stInfo = statoInfo(a.stato);
                  return (
                    <tr key={a.id_annuncio} data-testid={`le-mie-aste-row-${a.id_annuncio}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 max-w-[240px]">
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-[#EA7B2B] border-[#EA7B2B]/30 bg-[#EA7B2B]/10 mr-2">{catLottoLabel(a.categoria_lotto)}</span>
                        <div className="text-white mt-1">{a.citta}{a.provincia && a.provincia !== a.citta ? ` (${a.provincia})` : ""}</div>
                        <div className="text-xs text-zinc-500 truncate" title={a.indirizzo}>{a.indirizzo}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 font-mono text-xs whitespace-nowrap">
                        {a.data_vendita ? new Date(a.data_vendita).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        {esitoInfo && <div className="mt-1"><span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: esitoInfo.color, borderColor: `${esitoInfo.color}4d`, backgroundColor: `${esitoInfo.color}1a` }}>{esitoInfo.label}</span></div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-300 whitespace-nowrap">{eur(a.offerta_minima, 2)}</td>
                      <td className="px-4 py-3">
                        {stInfo ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: stInfo.color, borderColor: `${stInfo.color}4d`, backgroundColor: `${stInfo.color}1a` }}>{stInfo.label}</span> : <span className="text-zinc-600 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        {a.nota ? <span className="text-xs text-zinc-400 truncate block" title={a.nota}>{a.nota}</span> : <span className="text-zinc-700 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          <button onClick={() => togglePreferito(a)} data-testid={`le-mie-aste-star-${a.id_annuncio}`} title={a.preferito ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"} className={a.preferito ? "text-[#EA7B2B]" : "text-zinc-600 hover:text-[#EA7B2B]"}>
                            <Star className={`w-4 h-4 ${a.preferito ? "fill-[#EA7B2B]" : ""}`} />
                          </button>
                          <Link to={`/finder-aste/${a.id_annuncio}`} data-testid={`le-mie-aste-dettaglio-${a.id_annuncio}`} title="Apri scheda dettaglio" className="text-zinc-500 hover:text-[#EA7B2B]">
                            <Eye className="w-4 h-4" />
                          </Link>
                          <a href={a.url_annuncio} target="_blank" rel="noopener noreferrer" title="Vai all'annuncio su pvp.giustizia.it" className="text-zinc-500 hover:text-[#EA7B2B]">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
